/**
 * DSS Fuzzy Search v2
 * 
 * Features:
 * - Whole-word matching (not single letter matches)
 * - Product descriptions loaded via JSON API
 * - "Ganzen Shop durchsuchen" card when < 4 results
 * - Debounced input, accessible, text highlighting
 */
(function () {
  'use strict';

  const SELECTOR_FUZZY = '[data-js-dss-fuzzy]';
  const SELECTOR_INPUT = '[data-js-dss-fuzzy-input]';
  const SELECTOR_CLEAR = '[data-js-dss-fuzzy-clear]';
  const SELECTOR_COUNT = '[data-js-dss-fuzzy-count]';
  const SELECTOR_RESULTS = '[data-js-dss-fuzzy-results]';
  const SELECTOR_ITEM = '[data-js-product-item]';

  // ── Word-boundary matching ──

  function fuzzyScore(query, target) {
    if (!query || !target) return 0;

    const q = query.toLowerCase().trim();
    const t = target.toLowerCase().trim();

    // Exact match
    if (t === q) return 1;
    
    // Starts with
    if (t.startsWith(q)) return 0.95;

    // Whole-word match: split target into words, check each
    const tWords = t.split(/[\s,;.!?()\/\-]+/).filter(Boolean);
    for (const word of tWords) {
      if (word === q) return 0.98;
      if (word.startsWith(q)) return 0.92;
    }

    // Continuous substring match: "Sauer" matches "Sauerkraut" but NOT scattered letters
    if (t.includes(q)) return 0.85;

    // NO subsequence fallback — scattered letters must NOT match
    return 0;
  }

  function bestMatchScore(query, productData) {
    const fields = [
      { text: productData.title, weight: 1.0 },
      { text: productData.description, weight: 0.6 },
      { text: productData.vendor, weight: 0.5 },
      { text: productData.tags, weight: 0.4 }
    ];

    let bestScore = 0;
    for (const field of fields) {
      if (!field.text) continue;

      if (field.text.includes(',')) {
        const parts = field.text.split(',').map(s => s.trim());
        for (const part of parts) {
          const s = fuzzyScore(query, part) * field.weight;
          if (s > bestScore) bestScore = s;
        }
      } else {
        // For long text (descriptions), check each sentence
        if (field.text.length > 200) {
          const sentences = field.text.split(/[.!?]+/).filter(s => s.trim().length > 2);
          for (const sentence of sentences) {
            const s = fuzzyScore(query, sentence.trim()) * field.weight;
            if (s > bestScore) bestScore = s;
          }
        } else {
          const s = fuzzyScore(query, field.text) * field.weight;
          if (s > bestScore) bestScore = s;
        }
      }
    }

    return bestScore;
  }

  // ── Highlighting ──

  function highlightText(text, query) {
    if (!query || !text) return text;

    const q = query.toLowerCase().trim();
    const words = text.split(/(\s+)/);

    return words.map(word => {
      const lower = word.toLowerCase();
      if (lower.includes(q)) {
        const idx = lower.indexOf(q);
        return word.substring(0, idx) +
          '<mark class="dss-fuzzy-highlight">' + word.substring(idx, idx + q.length) + '</mark>' +
          word.substring(idx + q.length);
      }
      return word;
    }).join('');
  }

  // ── Product data ──

  function extractProductData(item) {
    const titleEl = item.querySelector('.product-item__title');
    const title = titleEl ? titleEl.textContent.trim() : '';
    const vendorEl = item.querySelector('.product-item__vendor');
    const vendor = vendorEl ? vendorEl.textContent.trim() : '';
    const tags = item.dataset.tags || '';
    const productType = item.dataset.productType || '';
    const description = item.dataset.description || '';

    return { title, vendor, tags, productType, description };
  }

  // ── Load descriptions from JSON ──

  let descriptionsLoaded = false;

  function loadDescriptions(grid) {
    if (descriptionsLoaded) return Promise.resolve();
    const items = grid.querySelectorAll(SELECTOR_ITEM);
    const ids = Array.from(items).map(i => {
      const id = i.id.replace('product-item-', '');
      return id;
    }).filter(Boolean);

    if (!ids.length) return Promise.resolve();

    // Load in batches of 50
    const batches = [];
    for (let i = 0; i < ids.length; i += 50) {
      batches.push(ids.slice(i, i + 50));
    }

    return Promise.all(batches.map(batch =>
      fetch('/products.json?limit=250')
        .then(r => r.json())
        .then(data => {
          if (!data.products) return;
          const map = {};
          data.products.forEach(p => {
            map[String(p.id)] = (p.body_html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
          });
          items.forEach(item => {
            const id = item.id.replace('product-item-', '');
            if (map[id]) {
              item.dataset.description = map[id].substring(0, 500);
            }
          });
          descriptionsLoaded = true;
        })
        .catch(() => {})
    ));
  }

  // ── "Ganzen Shop durchsuchen" card ──

  function createShopSearchCard(query, searchUrl, bgImg) {
    const card = document.createElement('div');
    card.className = 'product-item card dss-fuzzy-search__shop-card-item';
    card.dataset.jsProductItem = '';

    var fullUrl = searchUrl + encodeURIComponent(query);

    var bgStyle = bgImg ? 'background-image:url(' + bgImg + ');background-size:cover;background-position:center;' : '';

    card.innerHTML =
      '<a href="' + fullUrl + '" class="dss-search-card-link" style="' + bgStyle + '">' +
        '<div class="dss-search-card__overlay"></div>' +
        '<div class="dss-search-card__text">' +
          '<span>Gesamten Shop nach ' + escapeHtml(query) + ' durchsuchen</span>' +
        '</div>' +
      '</a>';

    return card;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ── Init ──

  function initFuzzySearch(container) {
    if (!container || container._dssFuzzyInit) return;
    container._dssFuzzyInit = true;

    const input = container.querySelector(SELECTOR_INPUT);
    const clearBtn = container.querySelector(SELECTOR_CLEAR);
    const countEl = container.querySelector(SELECTOR_COUNT);
    const resultsEl = container.querySelector(SELECTOR_RESULTS);
    const gridSelector = container.dataset.grid || '#main-collection-product-grid';
    const minChars = parseInt(container.dataset.minChars, 10) || 2;
    const threshold = parseFloat(container.dataset.threshold) || 0.4;
    const doHighlight = container.dataset.highlight !== 'false';
    const showCount = container.dataset.showCount !== 'false';
    const searchUrl = container.dataset.searchUrl || '/search?q=';
    const bgImage = container.dataset.bgImage || '';

    if (!input) return;

    let debounceTimer = null;
    let lastQuery = '';
    let productCache = null;

    function getProducts() {
      if (productCache) return productCache;
      const grid = document.querySelector(gridSelector);
      if (!grid) return [];
      productCache = Array.from(grid.querySelectorAll(SELECTOR_ITEM));
      return productCache;
    }

    function clearCache() {
      productCache = null;
      descriptionsLoaded = false;
    }

    function updateCount(visible, total) {
      if (!countEl || !showCount) return;
      if (visible === total || visible === 0) {
        countEl.textContent = '';
      } else {
        countEl.textContent = visible + ' von ' + total + ' Produkten';
      }
    }

    function applyFilter() {
      const query = input.value.trim();

      if (clearBtn) {
        clearBtn.style.display = query.length > 0 ? '' : 'none';
      }

      if (query.length < minChars) {
        resetProducts();
        if (resultsEl) resultsEl.style.display = 'none';
        return;
      }

      if (query === lastQuery) return;
      lastQuery = query;

      const grid = document.querySelector(gridSelector);
      if (grid) grid.classList.add('dss-smart-filters--loading');

      // Load descriptions first, then filter
      loadDescriptions(grid).then(() => {
        // Remove old card and reset flag before re-filter
        var oldCard = grid.querySelector('.dss-fuzzy-search__shop-card-item');
        if (oldCard) oldCard.remove();
        grid._dssSearching = false;

        const products = getProducts();
        if (!products.length) {
          if (grid) grid.classList.remove('dss-smart-filters--loading');
          return;
        }

        requestAnimationFrame(() => {
          let visibleCount = 0;
          const total = products.length;

          products.forEach(item => {
            const data = extractProductData(item);
            const score = bestMatchScore(query, data);
            const matches = score >= threshold;

            if (matches) {
              item.style.display = '';
              item.removeAttribute('aria-hidden');
              visibleCount++;

              if (doHighlight) {
                const titleEl = item.querySelector('.product-item__title span');
                if (titleEl && !titleEl.dataset.originalText) {
                  titleEl.dataset.originalText = titleEl.textContent;
                }
                if (titleEl && titleEl.dataset.originalText) {
                  titleEl.innerHTML = highlightText(titleEl.dataset.originalText, query);
                }
              }
            } else {
              item.style.display = 'none';
              item.setAttribute('aria-hidden', 'true');

              if (doHighlight) {
                const titleEl = item.querySelector('.product-item__title span');
                if (titleEl && titleEl.dataset.originalText) {
                  titleEl.textContent = titleEl.dataset.originalText;
                }
              }
            }
          });

          updateCount(visibleCount, total);

          // "Gesamten Shop durchsuchen" card in grid
          var oldCard = grid.querySelector('.dss-fuzzy-search__shop-card-item');
          if (oldCard) oldCard.remove();

          if (resultsEl) resultsEl.style.display = 'none';

          if (visibleCount > 0 && visibleCount < 4 && !grid._dssSearching) {
            grid._dssSearching = true;
            var card = createShopSearchCard(query, searchUrl, bgImage);
            grid.appendChild(card);
          }

          if (grid) grid.classList.remove('dss-smart-filters--loading');
        });
      });
    }

    function resetProducts() {
      lastQuery = '';
      var grid = document.querySelector(gridSelector);
      var products = getProducts();

      // Remove search card
      if (grid) {
        var oldCard = grid.querySelector('.dss-fuzzy-search__shop-card-item');
        if (oldCard) oldCard.remove();
      }

      products.forEach(item => {
        item.style.display = '';
        item.removeAttribute('aria-hidden');

        const titleEl = item.querySelector('.product-item__title span');
        if (titleEl && titleEl.dataset.originalText) {
          titleEl.textContent = titleEl.dataset.originalText;
        }
      });

      updateCount(products.length, products.length);
    }

    input.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(applyFilter, 300);
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        input.value = '';
        resetProducts();
        if (clearBtn) clearBtn.style.display = 'none';
        if (resultsEl) { resultsEl.style.display = 'none'; resultsEl.innerHTML = ''; }
      }
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        input.value = '';
        resetProducts();
        clearBtn.style.display = 'none';
        if (resultsEl) { resultsEl.style.display = 'none'; resultsEl.innerHTML = ''; }
        input.focus();
      });
    }

    const grid = document.querySelector(gridSelector);
    if (grid) {
      const observer = new MutationObserver(() => {
        if (grid._dssSearching) return;
        clearCache();
        if (input.value.trim().length >= minChars) {
          lastQuery = '';
          applyFilter();
        }
      });
      observer.observe(grid, { childList: true });
    }
  }

  function scan() {
    document.querySelectorAll(SELECTOR_FUZZY).forEach(initFuzzySearch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', scan);
})();
